import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Suspense } from '#src/atoms/Suspense/index.tsx';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconCaretLeft } from '#src/atoms/icons/system/iconCaretLeft.ts';
import { iconSearch } from '#src/atoms/icons/system/iconSearch.ts';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import {
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from '#src/atoms/ui/Popover/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { glossary } from '#src/lib/glossary.ts';
import { lazy } from '#src/lib/retry.ts';
import { MenuIconButton } from '#src/routing/MultiSportAppBar/MenuIconButton/index.tsx';
import { regularMaterial } from '#src/style/utils.tsx';

const MultiSportSearchBar = lazy(
  async () => import('#src/components/search/MultiSportSearchBar/index.tsx'),
  'MultiSportSearchBar'
);

const Top = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--unit);
  padding: var(--double-unit) var(--double-unit) 0;
  max-width: 100%;
`;

const Content = styled(Top)`
  position: relative;
  z-index: 100000;
  width: 400px;
  max-height: calc(
    var(--radix-popover-content-available-height, 100vh) - var(--double-unit)
  );
  overflow: hidden;
  flex-shrink: 0;
  border-radius: var(--radius-l);
  ${regularMaterial}
  border: 1px solid var(--c-nd-100);
  margin-top: var(--unit);
  &:not(.isDesktop) {
    top: 0px !important;
    left: 0px !important;
    flex-shrink: unset;
    max-width: var(--100vw);
    max-height: var(--radix-popover-content-available-height, 100vh);
    width: var(--100vw);
    height: 100vh;
    border-radius: 0;
    overflow: scroll;
  }
`;
export const ResponsiveSearchBar = () => {
  const { formatMessage } = useIntl();
  const { up: isDesktop } = useScreenSize('desktop');
  const [open, setOpen] = useState(false);
  const { postMessage, useNativeSearch } = useIsMobileApp();

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const toggleOpen = useCallback(() => {
    if (useNativeSearch) {
      postMessage('clickSearch');
    } else {
      setOpen(true);
    }
  }, [useNativeSearch, postMessage]);

  return (
    <PopoverRoot
      open={open}
      onOpenChange={useNativeSearch ? undefined : setOpen}
      modal
    >
      <PopoverTrigger asChild>
        <MenuIconButton
          size="medium"
          icon={iconSearch}
          onClick={toggleOpen}
          active={open}
          aria-label={formatMessage(glossary.search)}
        />
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent style={{ zIndex: 30 }} asChild>
          <Content className={classNames({ isDesktop })}>
            {!isDesktop && (
              <PopoverClose asChild>
                <IconButton
                  size="medium"
                  color="tertiary"
                  icon={iconCaretLeft}
                />
              </PopoverClose>
            )}
            <Suspense fallback={<LoadingIndicator />}>
              <MultiSportSearchBar onExit={!open} onSelect={onClose} />
            </Suspense>
          </Content>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  );
};
